import { useState } from 'react';

import { AdminApiError, resetUserPassword } from '@/lib/adminApi';

/** support-admin-proposal.md §4's "reason required" rule, mirrored client-side
 * so the button disables before a doomed request round-trips — the SERVER
 * still enforces the same floor (422 below it), this is only the UX. */
export const MIN_REASON_LENGTH = 10;

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain) return '***';
  const visible = local.slice(0, 1) || '*';
  return `${visible}***@${domain}`;
}

interface ResetPasswordDialogProps {
  userId: string;
  /** When the caller already has the user's address (e.g. a user-detail page
   * that has loaded the profile), the confirm copy names the masked
   * destination up front. Without it (this task's own minimal
   * /users/:id/actions route, which owns no profile fetch), the copy stays
   * generic — the real masked address still surfaces once the server
   * answers, via `onSuccess`. */
  knownEmail?: string;
  onClose: () => void;
  onSuccess: (deliveredTo: string) => void;
}

export function ResetPasswordDialog({ userId, knownEmail, onClose, onSuccess }: ResetPasswordDialogProps) {
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = reason.trim();
  const canSubmit = trimmed.length >= MIN_REASON_LENGTH && !sending;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    try {
      const result = await resetUserPassword(userId, trimmed);
      onSuccess(result.delivered_to);
      onClose();
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Could not send the temporary password.');
      setSending(false);
    }
  }

  const destinationCopy = knownEmail
    ? `A temporary password will be emailed to ${maskEmail(knownEmail)}.`
    : "A temporary password will be emailed to this user's registered address.";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Reset password"
      className="fixed inset-0 z-popover flex items-center justify-center bg-scrim/40 px-4"
    >
      <div className="w-full max-w-sm rounded-panel border border-border bg-surface-field p-6">
        <h2 className="text-h3 font-semibold text-fg-primary">Reset password</h2>
        <p className="mt-2 text-body text-fg-secondary">{destinationCopy}</p>

        <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="reset-reason">
          Reason (required, at least {MIN_REASON_LENGTH} characters)
        </label>
        <textarea
          id="reset-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          disabled={sending}
          className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
        />

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
          >
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-card px-4 py-2 text-body font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send temporary password'}
          </button>
        </div>
      </div>
    </div>
  );
}
